import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, Search, Trash2, ArrowRight, AlertTriangle } from "lucide-react";
import {
  listClients,
  createClient,
  deleteClient,
  formatBRDate,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const emptyForm = { name: "", document: "", email: "", phone: "" };

// Compute days until certificate expiry
const certStatus = (validade) => {
  if (!validade) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(validade + "T00:00:00");
  const days = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return { days, tone: "critical", label: `Vencido há ${Math.abs(days)}d` };
  if (days <= 10) return { days, tone: "critical", label: `Vence em ${days}d` };
  if (days <= 30) return { days, tone: "warning", label: `Vence em ${days}d` };
  return null;
};

export default function Clients() {
  const navigate = useNavigate();
  const { canEdit, isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      setClients(await listClients());
    } catch {
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Razão Social é obrigatória");
      return;
    }
    try {
      const created = await createClient(form);
      toast.success("Cliente criado — complete a ficha");
      setDialogOpen(false);
      navigate(`/clientes/${created.id}`);
    } catch {
      toast.error("Erro ao criar cliente");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteClient(confirmDelete.id);
      toast.success("Cliente excluído");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.document || "").toLowerCase().includes(q) ||
        (c.nome_fantasia || "").toLowerCase().includes(q) ||
        (c.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [clients, query]);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
            Clientes
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">
            Cadastro completo com ficha de processo e exportação em PDF.
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openNew}
                data-testid="add-client-button"
                className="bg-[#0F2942] hover:bg-[#173E63] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Novo Cliente</DialogTitle>
                <DialogDescription>
                  Informe os dados iniciais. Você poderá completar a ficha na próxima tela.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Razão Social *</Label>
                  <Input
                    id="name"
                    autoFocus
                    data-testid="client-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ex: Empresa XYZ Ltda"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="document">CNPJ</Label>
                  <Input
                    id="document"
                    data-testid="client-document-input"
                    value={form.document}
                    onChange={(e) => setForm({ ...form, document: e.target.value })}
                    placeholder="00.000.000/0000-00"
                    className="mt-1"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    data-testid="client-cancel-button"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    data-testid="client-submit-button"
                    className="bg-[#0F2942] hover:bg-[#173E63] text-white"
                  >
                    Criar e continuar
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="clients-search-input"
          placeholder="Buscar por nome ou CNPJ..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-clients">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {clients.length === 0
                ? "Nenhum cliente cadastrado ainda."
                : "Nenhum cliente corresponde à busca."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-semibold px-4 py-3">Razão Social</th>
                  <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">CNPJ</th>
                  <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Regime</th>
                  <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">
                    Certificado Digital
                  </th>
                  <th className="text-right font-semibold px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const cert = certStatus(c.validade_certificado);
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/clientes/${c.id}`)}
                      data-testid={`client-row-${c.id}`}
                    >
                      <td className="px-4 py-3 font-medium text-[#0F172A] dark:text-slate-100">
                        {c.name}
                        {c.nome_fantasia && (
                          <span className="block text-xs text-slate-400 mt-0.5">
                            {c.nome_fantasia}
                          </span>
                        )}
                        {(c.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {(c.tags || []).slice(0, 3).map((t) => (
                              <span key={t} data-testid={`client-tag-${c.id}-${t}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                {t}
                              </span>
                            ))}
                            {(c.tags || []).length > 3 && (
                              <span className="text-[10px] text-slate-400">+{c.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">
                        {c.document || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">
                        {c.regime_tributario || "—"}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {c.validade_certificado ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-slate-600 text-xs">
                              {c.tipo_certificado || "—"} • {formatBRDate(c.validade_certificado)}
                            </span>
                            {cert && (
                              <Badge
                                className={
                                  cert.tone === "critical"
                                    ? "bg-red-100 text-red-700 hover:bg-red-100 border-0 w-fit"
                                    : "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-0 w-fit"
                                }
                              >
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {cert.label}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/clientes/${c.id}`)}
                            data-testid={`open-client-${c.id}`}
                          >
                            Abrir
                            <ArrowRight className="h-3.5 w-3.5 ml-1" />
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmDelete(c)}
                              data-testid={`delete-client-${c.id}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todos os prazos vinculados a{" "}
              <b>{confirmDelete?.name}</b> também serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-client"
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
